// ============================================================
// OBRA RANKING ENGINE — Fase 8.3
// Pontuação e ranking de obras por categoria
// ============================================================
import type {
  ObraSnapshot, ObraRanking, CorporateRankingResult, RankingCategory,
} from '@/types/corporate'

// ─── Score calculators ────────────────────────────────────────

function scoreSeguranca(obra: ObraSnapshot): number {
  // Higher incidents/APRs = lower score
  const incidentPenalty = Math.min(50, obra.incidentes_ativos * 15)
  const aprPenalty      = Math.min(30, obra.aprs_vencidas * 10)
  return Math.max(0, 100 - incidentPenalty - aprPenalty)
}

function scoreProdutividade(obra: ObraSnapshot): number {
  // Efetivo vs avanco (heuristic: if avanco > 0 and efetivo > 0 → productive)
  if (obra.efetivo_hoje === 0) return 30
  const baseScore = Math.min(100, 50 + obra.avanco_real * 0.5)
  return Math.round(baseScore)
}

function scoreAvanco(obra: ObraSnapshot): number {
  return Math.min(100, Math.round(obra.avanco_real))
}

function scoreFornecedores(obra: ObraSnapshot): number {
  const supplyPenalty = Math.min(50, obra.supply_criticos * 12)
  return Math.max(0, 100 - supplyPenalty)
}

function scoreEstabilidade(obra: ObraSnapshot): number {
  const issues = obra.incidentes_ativos + obra.aprs_vencidas + obra.supply_criticos
  return Math.max(0, 100 - issues * 8)
}

function scoreRiscoGeral(obra: ObraSnapshot): number {
  // Composite: invert risk score
  const sst   = scoreSeguranca(obra)     * 0.35
  const prod  = scoreProdutividade(obra) * 0.25
  const suply = scoreFornecedores(obra)  * 0.20
  const stab  = scoreEstabilidade(obra)  * 0.20
  return Math.round(sst + prod + suply + stab)
}

// ─── Category → scorer mapping ────────────────────────────────
const SCORERS: Record<RankingCategory, (o: ObraSnapshot) => number> = {
  produtividade:       scoreProdutividade,
  seguranca:           scoreSeguranca,
  avanco:              scoreAvanco,
  fornecedores:        scoreFornecedores,
  desvios:             (o) => Math.max(0, 100 - Math.abs(o.avanco_real - 50) * 2),
  estabilidade:        scoreEstabilidade,
  risco_geral:         scoreRiscoGeral,
}

const CATEGORY_LABEL: Record<RankingCategory, string> = {
  produtividade: 'Produtividade',
  seguranca:     'Segurança SST',
  avanco:        'Avanço Físico',
  fornecedores:  'Suprimentos',
  desvios:       'Desvios',
  estabilidade:  'Estabilidade',
  risco_geral:   'Risco Geral',
}

// ─── Build ranking for one category ──────────────────────────
function buildCategoryRanking(
  obras:    ObraSnapshot[],
  category: RankingCategory,
): CorporateRankingResult {
  const scorer = SCORERS[category]

  const ranked: ObraRanking[] = obras
    .map(obra => {
      const score = scorer(obra)
      return {
        obraId:     obra.id,
        obraNome:   obra.nome,
        category,
        score,
        rank:       0,        // filled below
        delta:      0,        // would need historical data
        trend:      'stable' as const,
        highlights: buildHighlights(obra, category, score),
      }
    })
    .sort((a, b) => b.score - a.score)
    .map((r, i) => ({ ...r, rank: i + 1 }))

  const avg  = ranked.length ? Math.round(ranked.reduce((s, r) => s + r.score, 0) / ranked.length) : 0

  return {
    category,
    rankings:    ranked,
    avg,
    best:        ranked[0]              ?? null,
    worst:       ranked[ranked.length - 1] ?? null,
    evaluatedAt: Date.now(),
  }
}

function buildHighlights(
  obra:     ObraSnapshot,
  category: RankingCategory,
  score:    number,
): string[] {
  const h: string[] = []
  if (category === 'seguranca') {
    if (obra.incidentes_ativos > 0) h.push(`${obra.incidentes_ativos} incidente(s) aberto(s)`)
    if (obra.aprs_vencidas > 0)     h.push(`${obra.aprs_vencidas} APR(s) vencida(s)`)
    if (score >= 90)                h.push('SST exemplar')
  }
  if (category === 'fornecedores') {
    if (obra.supply_criticos > 0) h.push(`${obra.supply_criticos} material(is) crítico(s)`)
    else                          h.push('Suprimentos OK')
  }
  if (category === 'avanco') {
    h.push(`${obra.avanco_real.toFixed(1)}% avançado`)
  }
  return h
}

// ─── Enrich obra snapshots with computed risk ─────────────────
export function enrichObras(obras: ObraSnapshot[]): ObraSnapshot[] {
  return obras.map(obra => {
    const score = scoreRiscoGeral(obra)
    const level = score >= 76 ? 'critical' : score >= 51 ? 'high' : score >= 26 ? 'moderate' : 'low'
    return { ...obra, riskScore: score, riskLevel: level }
  })
}

// ─── Build all rankings ───────────────────────────────────────
export function buildAllRankings(obras: ObraSnapshot[]): CorporateRankingResult[] {
  const categories: RankingCategory[] = [
    'risco_geral', 'seguranca', 'produtividade', 'avanco', 'fornecedores', 'estabilidade',
  ]
  return categories.map(cat => buildCategoryRanking(obras, cat))
}

export function getRankingByCategory(
  rankings: CorporateRankingResult[],
  category: RankingCategory,
): CorporateRankingResult | null {
  return rankings.find(r => r.category === category) ?? null
}

export { CATEGORY_LABEL, SCORERS }
